/**
 * Outil « Planche de Galton » — calculs (pur).
 *
 * Une bille rebondit sur n rangées de clous : à chaque clou, elle part à
 * droite avec la probabilité p (succès). Le casier où elle tombe = nombre de
 * succès, donc X ~ B(n, p). Simulation par le moteur partagé (`ui/random`).
 */

import { rational, type ExactNumber } from '../../ui/exact';
import { bernoulliPath, binomCoeff, binomialLaw, binomialPmf } from '../../ui/random';

export interface GaltonState {
  bins: number[];
  total: number;
  /** chemin de la dernière bille (vrai = à droite) */
  lastPath: boolean[] | null;
}

export const MAX_BALLS = 100_000;

export function emptyBoard(n: number): GaltonState {
  return { bins: Array(n + 1).fill(0), total: 0, lastPath: null };
}

export function dropBalls(
  state: GaltonState,
  n: number,
  p: number,
  count: number,
  rng: () => number,
): GaltonState {
  const k = Math.min(count, MAX_BALLS - state.total);
  if (k <= 0) return state;
  const bins = [...state.bins];
  let lastPath: boolean[] | null = state.lastPath;
  for (let i = 0; i < k; i++) {
    const path = bernoulliPath(n, p, rng);
    bins[path.filter(Boolean).length]!++;
    lastPath = path;
  }
  return { bins, total: state.total + k, lastPath };
}

export interface BinomialStats {
  mean: number;
  variance: number;
  sd: number;
  law: number[];
}

export function binomialStats(n: number, p: number): BinomialStats {
  const variance = n * p * (1 - p);
  return { mean: n * p, variance, sd: Math.sqrt(variance), law: binomialLaw(n, p) };
}

/**
 * P(X = k) exacte quand p = 1/2 : C(n, k)/2ⁿ, écrite en fraction (63/256
 * plutôt que 0,24609375) ; sinon null (valeur décimale).
 */
export function exactPmf(n: number, k: number, p: number): ExactNumber | null {
  if (Math.abs(p - 0.5) > 1e-9) return null;
  let num = binomCoeff(n, k);
  let den = 2 ** n;
  while (num % 2 === 0 && den > 1) {
    num /= 2;
    den /= 2;
  }
  if (den === 1) return rational(num, 1);
  return {
    plain: `${num}/${den}`,
    tex: `\\frac{${num}}{${den}}`,
    atoms: [String(num), '÷', String(den)],
    value: num / den,
  };
}

export { binomialPmf, binomCoeff };

/** Moyenne des casiers observés (null si aucune bille). */
export function sampleMean(state: GaltonState): number | null {
  if (state.total === 0) return null;
  return state.bins.reduce((s, c, k) => s + c * k, 0) / state.total;
}

/**
 * Bienaymé-Tchebychev avec δ = 2σ : P(|X − E| ≥ 2σ) ≤ 1/4. Renvoie la
 * probabilité exacte (somme de la loi), la fréquence observée et les casiers
 * concernés.
 */
export function chebyshev(
  n: number,
  p: number,
  state: GaltonState,
): { exact: number; observed: number | null; outside: boolean[] } {
  const { mean, sd, law } = binomialStats(n, p);
  const outside = law.map((_, k) => Math.abs(k - mean) >= 2 * sd - 1e-12);
  const exact = law.reduce((s, v, k) => s + (outside[k] ? v : 0), 0);
  const hits = state.bins.reduce((s, c, k) => s + (outside[k] ? c : 0), 0);
  return { exact, observed: state.total ? hits / state.total : null, outside };
}
